import type { Session } from '@supabase/supabase-js'

export type ActingRole = 'admin' | 'employer' | 'job_seeker'
const ACT_ROLE_KEY = 'actingAsRole'
const ACT_UID_KEY  = 'actingAsUserId'
const ZERO_UUID    = '00000000-0000-0000-0000-000000000000'
const UUID_RE      = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const isUuid = (v?: string | null) => !!v && UUID_RE.test(v)

export function setActing(role: ActingRole, userId?: string) {
  localStorage.setItem(ACT_ROLE_KEY, role)
  if (userId && isUuid(userId)) localStorage.setItem(ACT_UID_KEY, userId)
  else localStorage.removeItem(ACT_UID_KEY)
  
  // Broadcast acting change event
  window.dispatchEvent(new CustomEvent('acting-changed', { detail: role }));
}

export function clearActing() {
  try {
    localStorage.removeItem(ACT_ROLE_KEY)
    localStorage.removeItem(ACT_UID_KEY)
    // Clear any additional acting-related keys
    localStorage.removeItem('actingSource')
    
    // Broadcast acting change event
    window.dispatchEvent(new CustomEvent('acting-changed', { detail: null }));
  } catch (_) {
    // Ignore localStorage errors (e.g., in private browsing)
  }
}

export function getActingRole(): ActingRole | null {
  return (localStorage.getItem(ACT_ROLE_KEY) as ActingRole | null) ?? null
}

/**
 * If no valid acting user is set, fall back to the current session user.
 * Never return the zero-uuid.
 */
export function getActingUserId(session: Session | null): string | null {
  const fromLs = localStorage.getItem(ACT_UID_KEY)
  if (isUuid(fromLs) && fromLs !== ZERO_UUID) return fromLs
  return session?.user?.id ?? null
}

// Convenience functions for role switching
export function setActingRole(role: 'employer' | 'job_seeker') {
  localStorage.setItem(ACT_ROLE_KEY, role);
  window.dispatchEvent(new CustomEvent('acting-changed', { detail: role }));
}

export function resetActing() {
  clearActing();
}

// Legacy function for backward compatibility
export function setActingLegacy(role: ActingRole) {
  localStorage.setItem(ACT_ROLE_KEY, role);
}
